/**
 * Phase 6b2 PR 1 — `useTeamHub` hook.
 *
 * Fetches the signed-in user's team (if any) along with their role
 * and the full roster (name + role per member). Drives the read-only
 * `/v2/team` surface (T1 no-team · T3 athlete-member · T4 trainer
 * empty roster · T5 trainer populated roster).
 *
 * Mirrors vanilla `loadMyTeam` + `loadTeamMembers`
 * (`src/renderer/js/team/team-management.js:255-296,491-528`).
 * Queries Supabase directly because no `/api/teams/:id` REST endpoint
 * exists yet (the only team endpoints in `api/index.js` are
 * `/api/team/create`, `/api/team/join`, `/api/team/kudos`).
 *
 * `useMyTeam` continues to exist for the legacy boolean-only consumer
 * (`<TrainingModesSection>`); this hook is the richer drop-in for
 * the team route.
 *
 * Writes (T2 accept / T6 create / T7 remove / T8 leave) ship in PR 2.
 * Kudos data (composer + feed) ships in PR 3 via its own hook
 * (`useTeamKudos`); this hook stays focused on roster.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthFetch } from './useAuthFetch';

/** A row from the `teams` table — vanilla parity. */
export interface TeamRow {
  id: string;
  name: string;
  /** Vanilla writes `sport` and `season` separately; we surface both
   *  in the header. Nullable in DB. */
  sport: string | null;
  season: string | null;
  /** Vanilla writes `invite_code`; the share-link UX in TeamHeader
   *  embeds it as `wai.run/join/<code>`. */
  invite_code: string | null;
  /** The user's role on this team — derived from team_memberships. */
  myRole: 'athlete' | 'trainer';
}

/** A roster row — one per active team_memberships record. */
export interface TeamMember {
  /** users.id — primary key. */
  userId: string;
  /** Display name from users.name; falls back to "Teammate" if empty. */
  name: string;
  /** team_memberships.role for this user on this team. */
  role: 'athlete' | 'trainer';
  /** True iff this row is the signed-in viewer's row. Drives the
   *  "me" left-rule (3px --signal) in the roster. */
  isMe: boolean;
}

/** A team the signed-in user is an active member of. Drives the
 *  ENG-4 multi-team switcher chip in `<TeamHeader>`. */
export interface AvailableTeamSummary {
  id: string;
  name: string;
  myRole: 'athlete' | 'trainer';
}

export interface UseTeamHubResult {
  team: TeamRow | null;
  members: TeamMember[];
  loading: boolean;
  /** Non-null on Supabase error. UI renders an error block; user can
   *  retry via `refetch()`. */
  error: Error | null;
  /** Manual refetch — used by the route's error-block retry button. */
  refetch: () => Promise<void>;
  /** ENG-5 fix · the user id for whom (team, members) was last loaded
   *  successfully. Consumers that drive redirects on `team === null`
   *  must also check `loadedForUserId === currentUserId` to confirm
   *  the null is definitive — otherwise it's stale state from the
   *  initial mount / EMPTY-return / pre-auth no-op load() branch,
   *  which would fire a false-positive redirect on first navigation
   *  to a sub-route like /v2/team/kudos. */
  loadedForUserId: string | null;
  /** ENG-4 fix · all active team memberships for the signed-in user
   *  (alphabetical by team name). Drives the `<TeamSwitcher>` chip
   *  which appears in `<TeamHeader>` when length > 1. Empty array
   *  means the user has no team (T1 no-team state). */
  availableTeams: AvailableTeamSummary[];
}

export interface UseTeamHubOptions {
  /** ENG-4 fix · explicit active team selection. When provided AND
   *  the id matches one of the user's active memberships, the hook
   *  loads that team's data instead of the default first-by-name
   *  membership. Typically driven by the `?team=<id>` URL search
   *  param via `useSearchParams` in the route. Unknown / missing id
   *  falls back to the default selection. */
  activeTeamId?: string;
}

/* ENG-5 fix · EMPTY.loading is `true` (not `false`) so the unauth
   no-op path doesn't briefly report "loaded with no team", which
   would trigger downstream route guards that read `!loading && team
   === null` as a definitive "no team" signal. Real route auth gates
   guard against unauthenticated reads before consumers reach the
   guards that care about (team, loading). */
const EMPTY: UseTeamHubResult = {
  team: null,
  members: [],
  loading: true,
  error: null,
  refetch: async () => {},
  loadedForUserId: null,
  availableTeams: [],
};

export function useTeamHub(options: UseTeamHubOptions = {}): UseTeamHubResult {
  const { activeTeamId } = options;
  const { supabase, user } = useAuthContext();
  const fetchAuthed = useAuthFetch();
  const [team, setTeam] = useState<TeamRow | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [availableTeams, setAvailableTeams] = useState<AvailableTeamSummary[]>(
    [],
  );
  /* ENG-5 fix · `loadedForUserId` flips to user.id after a successful
   * load completes for the current user. Until it matches the current
   * user.id, `team === null` is NOT yet a definitive "no team" signal
   * — consumers that drive redirects on team-null must gate against
   * this value too. Tracked as state (not ref) so it triggers the
   * re-render that lets the route's kudos-sub-route guard fire at
   * the correct moment (after the load resolves with no team). */
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);
  /* Inflight-load token. Each load() call captures the current user.id
   * and only writes state if the inflight token still matches when the
   * fetch resolves. Prevents a stale fetch (for a previous user) from
   * overwriting state for a newly-arrived user. */
  const inflightUserIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !user) {
      /* ENG-5 · DO NOT setLoading(false) here. Keeping `loading` true
       * while we wait for auth to settle prevents the brief
       * (team=null, loading=false) frame that the route's kudos-
       * sub-route guard would otherwise misread as "user has no team
       * → redirect to /team". Once auth settles we'll re-enter load()
       * with the real user. */
      setTeam(null);
      setMembers([]);
      setLoadedForUserId(null);
      return;
    }
    const tokenForThisLoad = user.id;
    inflightUserIdRef.current = tokenForThisLoad;
    setLoading(true);
    setError(null);
    try {
      // Step 1 — ENG-4 fix · fetch ALL active memberships (was
      // hardcoded `.limit(1)` for vanilla parity). The user may be
      // on multiple teams (athlete on one + trainer of another, or
      // member of multiple coaching squads); the switcher needs the
      // full list to render its dropdown.
      const { data: myMemberships, error: mErr } = await supabase
        .from('team_memberships')
        .select('team_id, role')
        .eq('user_id', user.id)
        .eq('status', 'active');
      if (mErr) throw mErr;

      const memberships = Array.isArray(myMemberships) ? myMemberships : [];

      if (memberships.length === 0) {
        /* Stale-fetch guard — if the user changed (signed out / in)
         * while this fetch was inflight, don't write state. */
        if (inflightUserIdRef.current !== tokenForThisLoad) return;
        setTeam(null);
        setMembers([]);
        setAvailableTeams([]);
        setLoadedForUserId(tokenForThisLoad);
        setLoading(false);
        return;
      }

      // Step 1b — fetch the names for ALL teams the user belongs to
      // so we can populate `availableTeams` for the switcher.
      const teamIds = memberships.map((m) => m.team_id as string);
      const { data: teamRows, error: teamsErr } = await supabase
        .from('teams')
        .select('id, name')
        .in('id', teamIds);
      if (teamsErr) throw teamsErr;
      const teamNameMap = new Map<string, string>();
      for (const t of teamRows ?? []) {
        teamNameMap.set(t.id as string, (t.name as string | null) ?? 'Team');
      }
      const summaries: AvailableTeamSummary[] = memberships
        .map((m) => ({
          id: m.team_id as string,
          name: teamNameMap.get(m.team_id as string) ?? 'Team',
          myRole:
            (m.role === 'trainer' ? 'trainer' : 'athlete') as
              | 'athlete'
              | 'trainer',
        }))
        // Alphabetical for stable rendering in the switcher dropdown.
        .sort((a, b) => a.name.localeCompare(b.name));

      // Pick the active membership: explicit `activeTeamId` prop
      // wins when it matches; otherwise the first by name (matches
      // vanilla parity for the default-team behavior). Falling back
      // when the supplied id is stale (e.g., the user just left
      // that team) prevents a permanent "team not found" state.
      const resolved =
        (activeTeamId &&
          summaries.find((s) => s.id === activeTeamId)) ||
        summaries[0];
      const teamId = resolved.id;
      const myRole = resolved.myRole;

      // Step 2 — fetch the team row (direct read, RLS: is_team_member) + the
      // roster (server endpoint) in parallel. A3: the roster + name lookup
      // moved server-side so the client no longer reads `team_memberships`
      // cross-user or the `users` table at all — `users` carries email +
      // Stripe/billing columns that must never cross the team boundary. The
      // endpoint returns only { userId, name, role } for active members.
      const [teamRes, membersRes] = await Promise.all([
        supabase
          .from('teams')
          .select('id, name, sport, season, invite_code')
          .eq('id', teamId)
          .maybeSingle(),
        fetchAuthed(`/api/team/members?teamId=${encodeURIComponent(teamId)}`),
      ]);
      if (teamRes.error) throw teamRes.error;
      if (!membersRes.ok) throw new Error(`roster fetch failed (${membersRes.status})`);

      const teamRow = teamRes.data;
      const membersJson = (await membersRes.json()) as {
        members?: Array<{
          userId: string;
          name: string | null;
          role: 'athlete' | 'trainer';
        }>;
      };
      const rosterRaw = Array.isArray(membersJson.members)
        ? membersJson.members
        : [];

      const roster: TeamMember[] = rosterRaw.map((r) => ({
        userId: r.userId,
        name: r.name ?? 'Teammate',
        role: r.role === 'trainer' ? 'trainer' : 'athlete',
        isMe: r.userId === user.id,
      }));

      // Stable ordering: me first, then trainers, then athletes by name.
      roster.sort((a, b) => {
        if (a.isMe !== b.isMe) return a.isMe ? -1 : 1;
        if (a.role !== b.role) return a.role === 'trainer' ? -1 : 1;
        return a.name.localeCompare(b.name);
      });

      /* Stale-fetch guard — abort write if the user changed mid-flight. */
      if (inflightUserIdRef.current !== tokenForThisLoad) return;
      setTeam(
        teamRow
          ? {
              id: teamRow.id as string,
              name: (teamRow.name as string | null) ?? 'Team',
              sport: teamRow.sport as string | null,
              season: teamRow.season as string | null,
              invite_code: teamRow.invite_code as string | null,
              myRole,
            }
          : null,
      );
      setMembers(roster);
      setAvailableTeams(summaries);
      setLoadedForUserId(tokenForThisLoad);
      setLoading(false);
    } catch (e) {
      if (inflightUserIdRef.current !== tokenForThisLoad) return;
      setError(e instanceof Error ? e : new Error(String(e)));
      setTeam(null);
      setMembers([]);
      setAvailableTeams([]);
      /* Error path keeps loadedForUserId null intentionally — the
       * data is NOT definitively resolved for this user; the error
       * banner + retry button handle the user-facing state. */
      setLoading(false);
    }
  }, [supabase, user, activeTeamId, fetchAuthed]);

  useEffect(() => {
    let unmounted = false;
    void (async () => {
      await load();
      // No-op if unmounted; setters are no-op after unmount.
      if (unmounted) return;
    })();
    return () => {
      unmounted = true;
    };
  }, [load]);

  if (!supabase || !user) return EMPTY;
  return {
    team,
    members,
    loading,
    error,
    refetch: load,
    loadedForUserId,
    availableTeams,
  };
}
